<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import AudienceService from '@/services/AudienceService'
import { QUESTION_TEXT_MAX } from '@/composables/useAudienceRoom'
import { useOptimisticActions } from '@/composables/useOptimisticActions'
import { viewerColor, viewerTag } from './viewerIdentity'
import FormButton from '@/components/Atoms/FormButton.vue'
import AudienceComposer from './AudienceComposer.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'

const props = defineProps({
	streamId: {
		type: String,
		required: true
	},
	questions: {
		type: Array,
		default: () => []
	},
	connected: {
		type: Boolean,
		default: false
	},
	/** Our own viewer id, so the host's own questions can be marked as theirs. */
	selfVid: {
		type: String,
		default: ''
	},
	canPost: {
		type: Boolean,
		default: false
	},
	/** Shown in place of the placeholder when posting is not possible. */
	blockedReason: {
		type: String,
		default: ''
	},
	sendError: {
		type: String,
		default: ''
	},
	/** Returns whether the frame left the socket. See useAudienceRoom.sendQuestion. */
	onSend: {
		type: Function,
		default: () => false
	}
})

const draft = ref('')
const order = ref('top')
const hideAnswered = ref(false)

// Re-rendered on a timer only so the relative times stay honest; a question
// asked "just now" that still says so twenty minutes later is worse than no
// timestamp at all.
const now = ref(Date.now())
const clock = window.setInterval(() => { now.value = Date.now() }, 30000)
onBeforeUnmount(() => window.clearInterval(clock))

// The same proxied round trip as chat moderation, so the same treatment:
// applied on click, reconciled when qa.update or qa.del lands.
const moderation = useOptimisticActions()

const answerKey = (id) => `qa.answer:${id}`
const deleteKey = (id) => `qa.del:${id}`

/**
 * The queue with the moderator's own actions already in it.
 *
 * Note what a rollback means here. The list is ordered, not positional — a
 * question sits where its answered flag and its upvotes put it — so "restore
 * the row where it was" is really "recompute the order without this action in
 * it". Overlaying and sorting downstream of the overlay gives exactly that: an
 * optimistically answered question sinks, and if the request is refused it
 * rises back to where the room's own numbers say it belongs, not to wherever
 * it happened to be on screen.
 */
const overlaid = computed(() => props.questions
	.filter((q) => !moderation.has(deleteKey(q.id)))
	.map((q) => (moderation.has(answerKey(q.id)) ? { ...q, answered: true } : q)))

const answeredCount = computed(() => overlaid.value.filter((q) => q.answered).length)

const visibleQuestions = computed(() => {
	const rows = hideAnswered.value ? overlaid.value.filter((q) => !q.answered) : [...overlaid.value]

	// Newest-first is a plain reverse of the room's own id order. Top puts the
	// work first: unanswered before answered, then most-supported, then oldest,
	// so an equal-vote tie does not shuffle on every tally.
	if (order.value === 'new') return rows.sort((a, b) => b.id - a.id)

	return rows.sort((a, b) => (Number(a.answered) - Number(b.answered))
		|| ((b.upvotes || 0) - (a.upvotes || 0))
		|| (a.id - b.id))
})

watch(() => props.questions, () => {
	const present = new Set(props.questions.map((q) => q.id))
	const answered = new Set(props.questions.filter((q) => q.answered).map((q) => q.id))

	moderation.settle((key) => {
		const id = Number(key.slice(key.indexOf(':') + 1))
		return key.startsWith('qa.del:') ? !present.has(id) : answered.has(id)
	})
}, { deep: true })

const post = () => {
	if (!props.canPost || !draft.value.trim()) return

	// The room broadcasts qa.new back to us, so the list fills itself; there is
	// nothing to render optimistically.
	if (props.onSend(draft.value)) draft.value = ''
}

/**
 * Coarse on purpose. A host working a queue mid-broadcast needs "is this from
 * the start of the session or from thirty seconds ago", not a precise clock.
 */
const askedAgo = (ts) => {
	if (!ts) return ''

	const seconds = Math.max(0, Math.round((now.value - ts) / 1000))
	if (seconds < 45) return 'just now'

	const minutes = Math.round(seconds / 60)
	if (minutes < 60) return `${minutes}m ago`

	const hours = Math.floor(minutes / 60)
	return `${hours}h ${minutes % 60}m ago`
}

const askedAt = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')

const markAnswered = (question) => moderation.run({
	key: answerKey(question.id),
	request: () => AudienceService.markQuestionAnswered(props.streamId, question.id),
	failure: 'The question is still open — marking it answered failed.'
})

const removeQuestion = (question) => moderation.run({
	key: deleteKey(question.id),
	request: () => AudienceService.deleteQuestion(props.streamId, question.id),
	failure: 'That question is back — deleting it failed.'
})
</script>

<template>
	<div class="flex flex-col h-full">
		<!-- Above the list rather than below it: the queue is ordered by
		unanswered-then-most-upvoted, not chronologically, so the foot of it is
		the least interesting place to leave the composer. -->
		<div class="shrink-0 mb-2">
			<AudienceComposer v-model="draft" :maxlength="QUESTION_TEXT_MAX" :can-post="canPost"
				placeholder="Add a question as the host…" :blocked-reason="blockedReason"
				:send-error="sendError" submit-label="Ask" :counter-at="60" @submit="post" />
		</div>

		<!-- The order was previously invisible, which makes a question moving
		after a vote look like a glitch rather than the queue working. -->
		<div v-if="overlaid.length" class="flex items-center gap-x-2 shrink-0 pb-2 mb-1
			border-b border-surface-3">
			<div class="flex rounded-md bg-surface-2 p-0.5" role="group" aria-label="Question order">
				<button v-for="mode in [{ id: 'top', label: 'Top voted' }, { id: 'new', label: 'Newest' }]"
					:key="mode.id" type="button" class="text-tiny px-2 py-1 rounded"
					:class="order === mode.id ? 'bg-surface-4 text-surface-text-1' : 'text-surface-7 hover:text-white'"
					:aria-pressed="order === mode.id" @click="order = mode.id">
					{{ mode.label }}
				</button>
			</div>

			<span class="text-tiny text-surface-7">
				{{ overlaid.length - answeredCount }} open<template v-if="answeredCount">, {{ answeredCount }} answered</template>
			</span>

			<button v-if="answeredCount" type="button"
				class="text-tiny text-surface-7 hover:text-white underline ml-auto"
				@click="hideAnswered = !hideAnswered">
				{{ hideAnswered ? 'Show answered' : 'Hide answered' }}
			</button>
		</div>

		<div class="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
			<p v-if="!visibleQuestions.length" class="text-sm text-surface-7 text-center py-8 mb-0">
				<template v-if="!connected">Connect to the room to see questions.</template>
				<template v-else-if="hideAnswered && answeredCount">
					Everything has been answered.
				</template>
				<template v-else>Nothing asked yet. Questions viewers submit land here, most-voted first.</template>
			</p>

			<div v-for="question in visibleQuestions" :key="question.id"
				class="group flex items-start gap-x-2.5 rounded-lg px-2.5 py-2 mb-1"
				:class="question.answered ? 'bg-surface-2/50' : 'bg-surface-2'">
				<!-- A count, not a card. At zero votes the old block was the
				loudest thing in a row whose content is the question. -->
				<span class="flex items-baseline gap-x-0.5 shrink-0 tabular-nums pt-0.5 min-w-[2.2rem]"
					:title="`${question.upvotes || 0} upvotes`"
					:class="question.upvotes ? 'text-surface-text-1' : 'text-surface-7'">
					<span class="text-tiny leading-none">▲</span>
					<span class="text-sm font-medium leading-none">{{ question.upvotes || 0 }}</span>
				</span>

				<div class="min-w-0 flex-1">
					<p class="flex items-center gap-x-1.5 text-tiny text-surface-7 leading-tight mb-0.5">
						<span class="w-1.5 h-1.5 rounded-full shrink-0"
							:style="{ backgroundColor: viewerColor(question.vid) }"></span>
						<span class="truncate max-w-[11rem]">{{ question.name || 'Guest' }}</span>
						<span class="font-mono shrink-0" :title="`Viewer ${question.vid}`">
							{{ viewerTag(question.vid) }}
						</span>
						<span v-if="selfVid && question.vid === selfVid" class="shrink-0">· Host</span>
						<span v-if="question.ts" class="shrink-0 ml-auto pl-1" :title="askedAt(question.ts)">
							{{ askedAgo(question.ts) }}
						</span>
					</p>

					<p class="text-sm break-words leading-snug mb-0" dir="auto"
						:class="question.answered ? 'text-surface-8' : 'text-surface-text-1'">
						{{ question.text }}
					</p>

					<p v-if="question.answered"
						class="flex items-center gap-x-1 text-tiny text-success leading-none mt-1 mb-0">
						<IconBase class="!w-3 !h-3"><IconCheckmark /></IconBase>
						Answered
					</p>
				</div>

				<div class="flex items-center gap-x-0.5 shrink-0">
					<FormButton v-if="!question.answered" type="light" size="sm" is-icon
						title="Mark as answered" @click="markAnswered(question)">
						<IconBase><IconCheckmark /></IconBase>
					</FormButton>
					<FormButton type="light" size="sm" is-icon title="Delete question"
						@click="removeQuestion(question)">
						<IconBase><IconBin /></IconBase>
					</FormButton>
				</div>
			</div>
		</div>
	</div>
</template>
